import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TransactionStatus } from 'src/app/enums/transaction.status';
import { TransactionType } from 'src/app/enums/transaction.type';
import { Bank } from 'src/app/model/bank';
import { Pagination } from 'src/app/model/Pagination';
import { Transaction } from 'src/app/model/transaction';
import { TransactionLog } from 'src/app/model/transaction.log';
import { CommonService } from 'src/app/services/common.service';
import { EnterpriseService } from 'src/app/services/enterprise.service';
import { LoginService } from 'src/app/services/login.service';
import { StorageService } from 'src/app/services/storage.service';
import { TransactionService } from 'src/app/services/transaction.service';
import { AppConstants } from 'src/app/settings/app-constants';
import { MessageConstant } from 'src/app/settings/message.constant';

@Component({
    selector: 'app-enterprise-transfer-to-bank',
    templateUrl: './enterprise-transfer-to-bank.component.html',
    styleUrls: ['./enterprise-transfer-to-bank.component.css'],
})
export class EnterpriseTransferToBankComponent implements OnInit, OnDestroy {
    private static readonly SYSTEM_BANK_WALLET_ID = 1;
    private static readonly TRANSACTION_TYPE_CODE = 'ttb';
    private static readonly NO_BANK_DETAILS_MESSAGE =
        'No bank details found for this enterprise. Please contact Lov.cash Admin.';
    private static readonly NO_AVAILABLE_BALANCE_MESSAGE =
        'There is no available balance to transfer.';
    private static readonly LOAD_FAILED_MESSAGE =
        'Unable to load transfer to bank data. Please try again.';

    private readonly destroy$ = new Subject<void>();

    dateFormat = AppConstants.DATE_FORMAT;
    disabled = true;
    search = false;
    displayView = 'none';
    displayCancel = 'none';
    fullBalance = 0;
    availableBalance = 0;
    transactionList: Transaction[] = [];
    transaction: Transaction = new Transaction();
    pagination = new Pagination();
    transactionStatusEnum = TransactionStatus;
    form: FormGroup;
    id = '';
    checkBalance: {
        maxTxnAmountAllowed?: number;
        status?: {
            message?: string;
        };
    } | null = null;
    showFullBalance = false;

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly enterpriseService: EnterpriseService,
        private readonly loginService: LoginService,
        private readonly transactionService: TransactionService,
        private readonly commonService: CommonService,
        private readonly storageService: StorageService,
        private readonly toastrService: ToastrService,
    ) {
        this.id = this.loginService.sessionUser.value?.enterprise?.enterpriseId;

        this.form = this.formBuilder.group({
            text: [''],
            transactionType: [TransactionType.transfer_to_bank],
            start: [''],
            length: [''],
            enterpriseId: [''],
        });
    }

    ngOnInit(): void {
        this.getData();

        this.form
            .get('text')
            ?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (value: string) => {
                    this.disabled = !value;
                },
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getData(): void {
        this.search = false;

        this.form.get('enterpriseId')?.setValue(this.id);
        this.form.get('start')?.setValue(this.pagination.start);
        this.form.get('length')?.setValue(this.pagination.length);

        this.enterpriseService.getEmployeeTransactionHistory(this.form).subscribe({
            next: (res) => {
                this.transactionList = res.data || [];
                this.pagination.total = res.total || 0;
                this.search = true;
            },
            error: () => {
                this.search = true;
                this.toastrService.error(
                    EnterpriseTransferToBankComponent.LOAD_FAILED_MESSAGE,
                    'Error',
                );
            },
        });

        this.enterpriseService.getEnterpriseProfile(this.id).subscribe({
            next: (res) => {
                this.storageService.setEnterpriseProfile(res.data);
                this.fullBalance = this.toNumber(res.data?.fullBalance);
                this.availableBalance = this.toNumber(res.data?.availableBalance);
            },
            error: () => {
                this.toastrService.error(
                    EnterpriseTransferToBankComponent.LOAD_FAILED_MESSAGE,
                    'Error',
                );
            },
        });
    }

    searchUsers(text: string): void {
        this.pagination.text = text;
        this.getData();
    }

    clearText(): void {
        this.form.get('text')?.setValue('');
    }

    receiveFilter(_data: { name: string; value: number }): void {
        // Required by child component output binding.
    }

    changeLength(len: number): void {
        this.pagination.length = len;
        this.pagination.start = 0;
        this.getData();
    }

    changePage(page: number): void {
        this.pagination.changePage(page);
        this.getData();
    }

    openViewModel(): void {
        this.displayView = 'block';
    }

    closeViewModel(): void {
        this.displayView = 'none';
    }

    openCancelModel(): void {
        this.displayCancel = 'block';
    }

    closeCancelModel(): void {
        this.displayCancel = 'none';
    }

    submit(): void {
        const bankDetailId = this.storageService.profileEnterprise.value?.bank?.bankDetailId;

        if (!bankDetailId) {
            this.toastrService.warning(
                EnterpriseTransferToBankComponent.NO_BANK_DETAILS_MESSAGE,
                'Bank details missing',
            );
            return;
        }

        if (this.availableBalance <= 0) {
            this.toastrService.info(
                EnterpriseTransferToBankComponent.NO_AVAILABLE_BALANCE_MESSAGE,
                'No balance',
            );
            return;
        }

        const createForm = this.formBuilder.group({
            amount: [this.availableBalance],
            fromWalletId: [this.loginService.sessionUser.value.walletId],
            transactionType: [TransactionType.transfer_to_bank],
            enterpriseId: [this.id],
            toWalletId: [EnterpriseTransferToBankComponent.SYSTEM_BANK_WALLET_ID],
            bankDetailId: [bankDetailId],
            transactionTypeCode: [EnterpriseTransferToBankComponent.TRANSACTION_TYPE_CODE],
        });

        this.clearText();

        this.commonService.checkUserBalance(createForm.value).subscribe({
            next: (res) => {
                if (res.success) {
                    this.createTransferToBank(createForm.value);
                    return;
                }

                if (res.hasEnoughBalance) {
                    this.toastrService.error(
                        MessageConstant.BANK_TRANSFER_REQUEST_FAILED,
                        'Transfer failed',
                    );
                    return;
                }

                this.showFullBalance = true;
                this.checkBalance = res;

                this.toastrService.warning(
                    res.status?.message || MessageConstant.BANK_TRANSFER_REQUEST_FAILED,
                    'Balance issue',
                );
            },
            error: () => {
                this.getData();
                this.toastrService.error(
                    MessageConstant.BANK_TRANSFER_REQUEST_FAILED,
                    'Transfer failed',
                );
            },
        });
    }

    cancelRequest(): void {
        this.closeCancelModel();

        const cancelForm = this.formBuilder.group({
            status: ['cancelled'],
            manualChangeReason: [''],
        });

        this.transactionService
            .updateTransferToBank(cancelForm.value, this.transaction.settlementRequestId)
            .subscribe({
                next: (res) => {
                    this.getData();

                    if (res.success) {
                        this.toastrService.success(
                            MessageConstant.BANK_TRANSFER_UPDATE_REQUEST_SUCCESS,
                            'Success',
                        );
                        return;
                    }

                    this.toastrService.error(
                        MessageConstant.BANK_TRANSFER_UPDATE_REQUEST_FAILED,
                        'Update failed',
                    );
                },
                error: () => {
                    this.getData();
                    this.toastrService.error(
                        MessageConstant.BANK_TRANSFER_UPDATE_REQUEST_FAILED,
                        'Update failed',
                    );
                },
            });
    }

    action(event: { type: string; value: Transaction }): void {
        this.transaction = event.value;

        if (!this.transaction.bankDetails) {
            this.transaction.bankDetails = new Bank();
        }

        if (!this.transaction.transactionLog) {
            this.transaction.transactionLog = new Array<TransactionLog>();
        }

        switch (event.type) {
            case 'view':
                this.openViewModel();
                break;
            case 'cancel':
                this.openCancelModel();
                break;
            default:
                break;
        }
    }

    setBalance(): void {
        if (this.checkBalance?.maxTxnAmountAllowed) {
            this.form.get('text')?.setValue(this.checkBalance.maxTxnAmountAllowed);
        }
    }

    private createTransferToBank(payload: object): void {
        this.enterpriseService.createTransferToBank(payload).subscribe({
            next: (res) => {
                this.getData();
                this.showFullBalance = false;

                if (res.success) {
                    this.toastrService.success(
                        MessageConstant.BANK_TRANSFER_REQUEST_SUCCESS,
                        'Success',
                    );
                    return;
                }

                this.toastrService.error(
                    MessageConstant.BANK_TRANSFER_REQUEST_FAILED,
                    'Transfer failed',
                );
            },
            error: () => {
                this.getData();
                this.toastrService.error(
                    MessageConstant.BANK_TRANSFER_REQUEST_FAILED,
                    'Transfer failed',
                );
            },
        });
    }

    private toNumber(value: string | number | null | undefined): number {
        const parsedValue = Number(value);
        return Number.isNaN(parsedValue) ? 0 : parsedValue;
    }
}
